import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CanvasService } from '@service/canvas/canvas.service';
import { EditorService } from '@service/editor.service';
import { VesselService } from '@service/vessel.service';
import { Mimic } from 'icreate/common/models/mimic';
import { Vessel } from 'icreate/common/models/vessel';
import { mimicValidator } from 'icreate/common/validators/mimic/mimic-validator';
import { Subject, takeUntil } from 'rxjs';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { RootItem, RootItemType } from 'icreate/common/models/mimic-folder';

@Component({
    selector: 'app-canvas-design-options',
    templateUrl: './canvas-design-options.component.html',
    styleUrls: ['./canvas-design-options.component.css', './../design-side-menu/design-side-menu.component.css'],
    standalone: false,
})
export class CanvasDesignOptionsComponent implements OnInit, OnDestroy {
    @Input() selectedMimic: Mimic;
    @Input() rootList: RootItem[];

    showColorOptions: boolean = false;
    interval: any;

    currentVessel: Vessel;
    titleError: string;
    private destroySubscriptionSubject$ = new Subject<void>();
    validationErrors: ValidationErrorMap = {};

    constructor(
        private canvasService: CanvasService,
        private editorService: EditorService,
        private vesselService: VesselService,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.editorService.currentMimic$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(mimic => {
            const canvas: any = this.canvasService.getCanvas();
            if (!mimic) {
                this.showColorOptions = false;
                const gridData = {
                    grid_color: '#000000',
                    grid_spacing: 1,
                    show_grid: false,
                };
                localStorage.setItem('grid_data', JSON.stringify(gridData));
                canvas.curConfig.gridSnapping = false;
                canvas.runExtensions('gridUpdate');
                return;
            }
            const background = document.getElementById('canvasBackground');
            const fill = background.children[0].getAttribute('fill');
            if (fill && fill !== 'none') this.showColorOptions = true;
            this.updateGrid();
            this.cdr.detectChanges();
        });

        this.currentVessel = this.vesselService.getActiveVessel();
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    mimicFillIconClick() {
        this.showColorOptions = !this.showColorOptions;
        if (!this.showColorOptions) {
            const background = document.getElementById('canvasBackground');
            if (background) {
                background.children[0].setAttribute('fill', 'none');
            }
        } else {
            this.updateBackground(this.selectedMimic.profile.bkcolor);
        }
    }

    gridInputButtonClick(event: 'minus' | 'plus') {
        if (!this.selectedMimic?.profile.allowGrid) {
            return;
        }
        if (!this.selectedMimic.profile.grid && this.selectedMimic.profile.grid !== 0)
            this.selectedMimic.profile.grid = 0;
        if (event === 'minus' && this.selectedMimic.profile.grid > 0.1) {
            if (this.selectedMimic.profile.grid > 0 && this.selectedMimic.profile.grid <= 1)
                this.selectedMimic.profile.grid -= 0.1;
            else this.selectedMimic.profile.grid -= 1;

            if (this.interval) return;
            this.startInterval(event);
        } else if (event === 'plus' && this.selectedMimic.profile.grid < 200) {
            if (this.selectedMimic.profile.grid >= 0 && this.selectedMimic.profile.grid < 1)
                this.selectedMimic.profile.grid += 0.1;
            else this.selectedMimic.profile.grid += 1;

            if (this.interval) return;
            this.startInterval(event);
        }
        this.updateGrid();
    }

    gridToggleChange() {
        this.selectedMimic.profile.allowGrid = !this.selectedMimic.profile.allowGrid;
        this.updateGrid();
    }

    updateBackground(newColor) {
        this.selectedMimic.profile.bkcolor = newColor;
        const background = document.getElementById('canvasBackground');
        if (background) {
            background.children[0].setAttribute('fill', newColor);
        }
    }

    updateGridColor(newColor) {
        this.selectedMimic.profile.color = newColor;
        this.updateGrid();
    }

    updateGrid() {
        const canvas: any = this.canvasService.getCanvas();
        const min: number = 0.1;
        const max: number = 200;
        const gridData = {
            grid_color: this.selectedMimic.profile.color || '#000000',
            grid_spacing: Number(this.selectedMimic.profile.grid.toFixed(2)) || 1,
            show_grid: this.selectedMimic.profile.allowGrid,
        };
        if (gridData.grid_spacing < min) {
            gridData.grid_spacing = min;
        }
        if (max < gridData.grid_spacing) {
            gridData.grid_spacing = max;
        }

        localStorage.setItem('grid_data', JSON.stringify(gridData));
        canvas.curConfig.gridSnapping = this.selectedMimic.profile.allowGrid;
        canvas.curConfig.snappingStep = gridData.grid_spacing > 0 ? gridData.grid_spacing : 1;
        canvas.runExtensions('gridUpdate');
    }

    public get validatorFunction() {
        return mimicValidator;
    }

    handleInputChange(event) {
        if (!Number(event.value)) {
            return;
        }
        this.selectedMimic.profile.grid = Number(event.value);
        this.updateGrid();
    }

    handleNameChange(newName: string) {
        this.selectedMimic.name = newName;
        this.validateMimicName();
    }

    validateMimicName(): boolean {
        const validationData = { name: this.selectedMimic.name };

        const errors = getValidationErrors(mimicValidator(), validationData);
        const mappedErrors: { [key: string]: { message: string } } = mapErrors(errors) || {};

        if (!this.rootList || this.rootList.length == 0) return;

        const mimics: Mimic[] = [];
        this.rootList?.forEach(item => {
            if (item.type === RootItemType.mimic) {
                mimics.push(item.mimic);
            } else if (item.type === RootItemType.folder) {
                mimics.push(...(item.folder?.mimicList ?? []));
            }
        });

        if (mimics.length == 0) return;

        const isRepeated = mimics.some(
            mimic => mimic.id !== this.selectedMimic.id && mimic.name == this.selectedMimic.name
        );

        if (isRepeated) {
            mappedErrors['name'] = {
                message: this.translateService.instant('mimic.create.error.title.repeat'),
            };
        }

        const hasErrors = Object.keys(mappedErrors).length > 0;
        this.validationErrors = hasErrors ? mappedErrors : null;

        this.cdr.detectChanges();

        return hasErrors;
    }

    onMouseUp() {
        clearInterval(this.interval);
        this.interval = null;
    }

    startInterval(event) {
        this.interval = setInterval(() => {
            this.gridInputButtonClick(event);
            this.updateGrid();
        }, 100);
    }

    get gridValue() {
        return +(this.selectedMimic?.profile?.grid).toFixed(2);
    }
}
